/* import __COLOCATED_TEMPLATE__ from './usage-management.hbs'; */
/* RESPONSIBLE TEAM: team-self-serve */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import type FinUsageService from 'embercom/services/fin-usage-service';

interface Signature {
  Element: HTMLDivElement;
  Args: {
    accordion: $TSFixMe;
    isReadOnly: boolean;
    openUsageDrawer: () => void;
  };
}

type StateConfig = {
  pillCopyKey?: string;
  pillColorClass?: string;
  titleCopyKey: string;
  titleSupportCopyKey?: string;
  descriptionPrimaryCopyKey: string;
  descriptionSecondaryCopyKey?: string;
  ctaCopyKey?: string;
};

const STATE_TO_CONFIG_MAP: Record<string, StateConfig> = {
  free_trial: {
    pillCopyKey: 'operator.workflows.visual-builder.usage-management.pill.free-during-trial',
    pillColorClass: 'bg-paywall-container',
    titleCopyKey: 'operator.workflows.visual-builder.usage-management.title.free-trial',
    descriptionPrimaryCopyKey:
      'operator.workflows.visual-builder.usage-management.description.free-trial-primary',
    descriptionSecondaryCopyKey:
      'operator.workflows.visual-builder.usage-management.description.free-trial-secondary',
  },
  limit_approaching: {
    pillCopyKey: 'operator.workflows.visual-builder.usage-management.pill.limit-approaching',
    pillColorClass: 'bg-notice-container',
    titleCopyKey: 'operator.workflows.visual-builder.usage-management.title.limit-approaching',
    titleSupportCopyKey:
      'operator.workflows.visual-builder.usage-management.title-support.usage-and-limit',
    descriptionPrimaryCopyKey:
      'operator.workflows.visual-builder.usage-management.description.limit-approaching-primary',
    descriptionSecondaryCopyKey:
      'operator.workflows.visual-builder.usage-management.description.limit-approaching-secondary',
    ctaCopyKey: 'operator.workflows.visual-builder.usage-management.cta.extend-usage-limit',
  },
  limit_reached: {
    pillCopyKey: 'operator.workflows.visual-builder.usage-management.pill.limit-reached',
    pillColorClass: 'bg-error-container',
    titleCopyKey: 'operator.workflows.visual-builder.usage-management.title.limit-reached',
    descriptionPrimaryCopyKey:
      'operator.workflows.visual-builder.usage-management.description.limit-reached-primary',
    descriptionSecondaryCopyKey:
      'operator.workflows.visual-builder.usage-management.description.limit-reached-secondary',
    ctaCopyKey: 'operator.workflows.visual-builder.usage-management.cta.extend-usage-limit',
  },
  limit_set: {
    titleCopyKey: 'operator.workflows.visual-builder.usage-management.title.limit-set',
    titleSupportCopyKey:
      'operator.workflows.visual-builder.usage-management.title-support.usage-and-limit',
    descriptionPrimaryCopyKey:
      'operator.workflows.visual-builder.usage-management.description.limit-set',
    ctaCopyKey: 'operator.workflows.visual-builder.usage-management.cta.set-alerts-and-limits',
  },
  no_limit_set: {
    titleCopyKey: 'operator.workflows.visual-builder.usage-management.title.no-limit-set',
    titleSupportCopyKey:
      'operator.workflows.visual-builder.usage-management.title-support.no-limit-set',
    descriptionPrimaryCopyKey:
      'operator.workflows.visual-builder.usage-management.description.no-limit-set',
    ctaCopyKey: 'operator.workflows.visual-builder.usage-management.cta.set-alerts-and-limits',
  },
} as const;

export default class UsageManagement extends Component<Signature> {
  @service declare finUsageService: FinUsageService;
  @service declare intercomEventService: $TSFixMe;

  get usageState() {
    if (this.finUsageService.isInTrial) {
      return 'free_trial';
    }

    if (this.finUsageService.isFinResolutionsLimitReached) {
      return 'limit_reached';
    }

    if (this.finUsageService.areUsedResolutions90PercentOfLimit) {
      return 'limit_approaching';
    }

    if (this.finUsageService.isFinUsageLimitEnabled) {
      return 'limit_set';
    }

    return 'no_limit_set';
  }

  get config() {
    return STATE_TO_CONFIG_MAP[this.usageState];
  }

  get showPill() {
    return !!this.config.pillCopyKey;
  }

  get showCta() {
    return !!this.config.ctaCopyKey;
  }

  get pillCopy() {
    return this.config.pillCopyKey || '';
  }

  get pillColorClass() {
    return this.config.pillColorClass || '';
  }

  get titleSupportCopy() {
    return this.config.titleSupportCopyKey || '';
  }

  get descriptionPrimaryCopy() {
    return this.config.descriptionPrimaryCopyKey || '';
  }

  get descriptionSecondaryCopy() {
    return this.config.descriptionSecondaryCopyKey || '';
  }

  get ctaCopy() {
    return this.config.ctaCopyKey || '';
  }

  get resolutionsUsedThisPeriod() {
    return this.finUsageService.finResolutionsInCurrentCycle;
  }

  get resolutionsSetLimit() {
    return this.finUsageService.finResolutionsLimit;
  }

  get daysRemainingInPeriod() {
    return this.finUsageService.daysLeftInBillingPeriod;
  }

  @action
  handleCtaClick() {
    this.trackAnalyticsEvent('clicked', 'set_alerts_and_limits_cta');
    this.args.openUsageDrawer();
  }

  @action
  handleClose(closeFn: () => void) {
    this.trackAnalyticsEvent('clicked', 'close_button');
    closeFn();
  }

  trackAnalyticsEvent(action: string, object: string) {
    this.intercomEventService.trackAnalyticsEvent({
      action,
      object,
      context: 'automation.workflows',
      section: 'fin_usage_management_accordion',
      state: this.usageState,
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Workflows::GraphEditor::NodeItems::Steps::Fin::Sections::UsageManagement': typeof UsageManagement;
  }
}
